/**
 * Generated by orval 🍺
 * Do not edit manually.
 * CommunityCliQ API
 *  ## Authentication
 *
 *   Every request must include an API access token generated in the CommunityCliQ \
 *   application:
 *
 *   ```
 *   Authorization: X-API-TOKEN
 *   ```
 *
 * OpenAPI spec version: V2.X
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import type {
  CommandErrorResponse,
  GetSettings1200,
  UnsubscribedNetworkResponse,
  UpdateSettingBody,
} from "../../model";

import { customInstance } from "../../../lib/SpringAxios";
import type { ErrorType } from "../../../lib/SpringAxios";

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === "queryKey") {
      continue;
    }
    Object.defineProperty(result, key, {
      configurable: true,
      enumerable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Unsubscribe current user from a network.
 * @summary Unsubscribe current user from a network.
 */
export const unsubscribeFromNetwork = (
  networkId: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    {
      method: "PUT",
      signal,
      url: `/api/v2/users/me/unsubscribed-list/${networkId}`,
    },
    options,
  );
};

export const getUnsubscribeFromNetworkMutationKey = () =>
  ["unsubscribeFromNetwork"] as const;

export const getUnsubscribeFromNetworkMutationOptions = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof unsubscribeFromNetwork>>,
    TError,
    UnsubscribeFromNetworkMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof unsubscribeFromNetwork>>,
  TError,
  UnsubscribeFromNetworkMutationVariables,
  TContext
> => {
  const mutationKey = getUnsubscribeFromNetworkMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof unsubscribeFromNetwork>>,
    UnsubscribeFromNetworkMutationVariables
  > = (props) => {
    const { networkId } = props ?? {};

    return unsubscribeFromNetwork(networkId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UnsubscribeFromNetworkMutationResult = NonNullable<
  Awaited<ReturnType<typeof unsubscribeFromNetwork>>
>;

export type UnsubscribeFromNetworkMutationError =
  ErrorType<CommandErrorResponse>;
export type UnsubscribeFromNetworkMutationVariables = { networkId: string };

/**
 * @summary Unsubscribe current user from a network.
 */
export const useUnsubscribeFromNetwork = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof unsubscribeFromNetwork>>,
      TError,
      UnsubscribeFromNetworkMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof unsubscribeFromNetwork>>,
  TError,
  UnsubscribeFromNetworkMutationVariables,
  TContext
> => {
  return useMutation(
    getUnsubscribeFromNetworkMutationOptions(options),
    queryClient,
  );
};
/**
 * Resubscribe current user to a network.
 * @summary Resubscribe current user to a network.
 */
export const resubscribeToNetwork = (
  networkId: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    {
      method: "DELETE",
      signal,
      url: `/api/v2/users/me/unsubscribed-list/${networkId}`,
    },
    options,
  );
};

export const getResubscribeToNetworkMutationKey = () =>
  ["resubscribeToNetwork"] as const;

export const getResubscribeToNetworkMutationOptions = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resubscribeToNetwork>>,
    TError,
    ResubscribeToNetworkMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof resubscribeToNetwork>>,
  TError,
  ResubscribeToNetworkMutationVariables,
  TContext
> => {
  const mutationKey = getResubscribeToNetworkMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof resubscribeToNetwork>>,
    ResubscribeToNetworkMutationVariables
  > = (props) => {
    const { networkId } = props ?? {};

    return resubscribeToNetwork(networkId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ResubscribeToNetworkMutationResult = NonNullable<
  Awaited<ReturnType<typeof resubscribeToNetwork>>
>;

export type ResubscribeToNetworkMutationError = ErrorType<CommandErrorResponse>;
export type ResubscribeToNetworkMutationVariables = { networkId: string };

/**
 * @summary Resubscribe current user to a network.
 */
export const useResubscribeToNetwork = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof resubscribeToNetwork>>,
      TError,
      ResubscribeToNetworkMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof resubscribeToNetwork>>,
  TError,
  ResubscribeToNetworkMutationVariables,
  TContext
> => {
  return useMutation(
    getResubscribeToNetworkMutationOptions(options),
    queryClient,
  );
};
/**
 * Get the value of a user setting.
 * @summary Get the value of a user setting.
 */
export const getSetting = (
  key: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<unknown>(
    { method: "GET", signal, url: `/api/v2/users/me/settings/${key}` },
    options,
  );
};

export const getGetSettingQueryKey = (key: string) => {
  return [`/api/v2/users/me/settings/${key}`] as const;
};

export const getGetSettingQueryOptions = <
  TData = Awaited<ReturnType<typeof getSetting>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  key: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSetting>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSettingQueryKey(key);

  const queryFn: QueryFunction = ({ signal }) =>
    getSetting(key, requestOptions, signal);

  return {
    enabled: key !== null && key !== undefined,
    queryFn,
    queryKey,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getSetting>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSettingQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSetting>>
>;
export type GetSettingQueryError = ErrorType<CommandErrorResponse>;

export function useGetSetting<
  TData = Awaited<ReturnType<typeof getSetting>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  key: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSetting>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSetting>>,
          TError,
          Awaited<ReturnType<typeof getSetting>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSetting<
  TData = Awaited<ReturnType<typeof getSetting>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  key: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSetting>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSetting>>,
          TError,
          Awaited<ReturnType<typeof getSetting>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSetting<
  TData = Awaited<ReturnType<typeof getSetting>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  key: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSetting>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get the value of a user setting.
 */

export function useGetSetting<
  TData = Awaited<ReturnType<typeof getSetting>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  key: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSetting>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetSettingQueryOptions(key, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Update the value of a user setting.
 * @summary Update the value of a user setting.
 */
export const updateSetting = (
  key: string,
  updateSettingBody?: UpdateSettingBody,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<void>(
    {
      data: updateSettingBody,
      headers: { "Content-Type": "application/json" },
      method: "PUT",
      signal,
      url: `/api/v2/users/me/settings/${key}`,
    },
    options,
  );
};

export const getUpdateSettingMutationKey = () => ["updateSetting"] as const;

export const getUpdateSettingMutationOptions = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateSetting>>,
    TError,
    UpdateSettingMutationVariables,
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateSetting>>,
  TError,
  UpdateSettingMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateSettingMutationKey();
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateSetting>>,
    UpdateSettingMutationVariables
  > = (props) => {
    const { data, key } = props ?? {};

    return updateSetting(key, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateSettingMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateSetting>>
>;
export type UpdateSettingMutationBody = UpdateSettingBody | undefined;
export type UpdateSettingMutationError = ErrorType<CommandErrorResponse>;
export type UpdateSettingMutationVariables = {
  key: string;
  data?: UpdateSettingBody;
};

/**
 * @summary Update the value of a user setting.
 */
export const useUpdateSetting = <
  TError = ErrorType<CommandErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateSetting>>,
      TError,
      UpdateSettingMutationVariables,
      TContext
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateSetting>>,
  TError,
  UpdateSettingMutationVariables,
  TContext
> => {
  return useMutation(getUpdateSettingMutationOptions(options), queryClient);
};
/**
 * Get networks that current user unsubscribed from.
 * @summary Get networks that current user unsubscribed from.
 */
export const getUnsubscribedNetworks = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<UnsubscribedNetworkResponse[]>(
    { method: "GET", signal, url: `/api/v2/users/me/unsubscribed-list` },
    options,
  );
};

export const getGetUnsubscribedNetworksQueryKey = () => {
  return [`/api/v2/users/me/unsubscribed-list`] as const;
};

export const getGetUnsubscribedNetworksQueryOptions = <
  TData = Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
  TError = ErrorType<CommandErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
      TError,
      TData
    >
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUnsubscribedNetworksQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUnsubscribedNetworks>>
  > = ({ signal }) => getUnsubscribedNetworks(requestOptions, signal);

  return { queryFn, queryKey, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetUnsubscribedNetworksQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUnsubscribedNetworks>>
>;
export type GetUnsubscribedNetworksQueryError = ErrorType<CommandErrorResponse>;

export function useGetUnsubscribedNetworks<
  TData = Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
          TError,
          Awaited<ReturnType<typeof getUnsubscribedNetworks>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUnsubscribedNetworks<
  TData = Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
          TError,
          Awaited<ReturnType<typeof getUnsubscribedNetworks>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetUnsubscribedNetworks<
  TData = Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get networks that current user unsubscribed from.
 */

export function useGetUnsubscribedNetworks<
  TData = Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getUnsubscribedNetworks>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetUnsubscribedNetworksQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * Get the map of all user settings.
 *
 *  <p>This method will never return <code>null</code>. If the user does not have any settings, an empty
 *  map will be returned.
 * @summary Get the map of all user settings
 */
export const getSettings1 = (
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<GetSettings1200>(
    { method: "GET", signal, url: `/api/v2/users/me/settings` },
    options,
  );
};

export const getGetSettings1QueryKey = () => {
  return [`/api/v2/users/me/settings`] as const;
};

export const getGetSettings1QueryOptions = <
  TData = Awaited<ReturnType<typeof getSettings1>>,
  TError = ErrorType<CommandErrorResponse>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getSettings1>>, TError, TData>
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetSettings1QueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getSettings1>>> = ({
    signal,
  }) => getSettings1(requestOptions, signal);

  return { queryFn, queryKey, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getSettings1>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSettings1QueryResult = NonNullable<
  Awaited<ReturnType<typeof getSettings1>>
>;
export type GetSettings1QueryError = ErrorType<CommandErrorResponse>;

export function useGetSettings1<
  TData = Awaited<ReturnType<typeof getSettings1>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSettings1>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSettings1>>,
          TError,
          Awaited<ReturnType<typeof getSettings1>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSettings1<
  TData = Awaited<ReturnType<typeof getSettings1>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSettings1>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSettings1>>,
          TError,
          Awaited<ReturnType<typeof getSettings1>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSettings1<
  TData = Awaited<ReturnType<typeof getSettings1>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSettings1>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get the map of all user settings
 */

export function useGetSettings1<
  TData = Awaited<ReturnType<typeof getSettings1>>,
  TError = ErrorType<CommandErrorResponse>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getSettings1>>, TError, TData>
    >;
    request?: SecondParameter<typeof customInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetSettings1QueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}
